import type { JSX } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import { AppProvider } from './context/AppContext';
import { PostHogIdentifyBridge } from './components/observability/PostHogIdentifyBridge';
import AdminGate from './components/AdminGate';
import AppShell from './components/layout/AppShell';
import Placeholder from './components/Placeholder';
import FeedbackInbox from './pages/FeedbackInbox';
import EngineAtlas from './pages/EngineAtlas';
import EngineAtlasInspectRouter from './components/atlas/inspect/EngineAtlasInspectRouter';
import EngineEditor from './pages/EngineEditor';
import EngineEditorWeightComponentsPage from './pages/EngineEditorWeightComponentsPage';
import ErrorLogViewer from './pages/ErrorLogViewer';
import BuildTracker from './pages/BuildTracker';
import AlgopixJobMonitor from './pages/AlgopixJobMonitor';
import SystemHealthDashboard from './pages/SystemHealthDashboard';
import UserManagement from './pages/UserManagement';
import DocsCMS from './pages/DocsCMS';
import ReleaseNotesManager from './pages/ReleaseNotesManager';
import WeeklyDigest from './pages/WeeklyDigest';
import JobRunsViewer from './pages/JobRunsViewer';

// arbi-dev route surface. All routes use /admin/* prefix per ADR 37 + the
// existing send-feedback Edge Function which emits /admin/feedback?id=...
// deep links. Default landing is /admin/feedback.
//
// Day 62a ships every route as <Placeholder /> with a "Coming in Day NN"
// note. Day 62b lands the real Feedback Inbox; Days 63-65 light up the
// remaining nine surfaces.
export default function App(): JSX.Element {
  return (
    <BrowserRouter>
      <AppProvider>
        <PostHogIdentifyBridge />
        <AdminGate>
          <Routes>
            <Route element={<AppShell />}>
              <Route index element={<Navigate to="/admin/feedback" replace />} />
              <Route path="admin/feedback" element={<FeedbackInbox />} />
              <Route
                path="admin/backlog"
                element={<Placeholder surface="Backlog Manager" comingIn="a post-launch release" />}
              />
              <Route path="admin/engine-atlas" element={<EngineAtlas />} />
              <Route
                path="admin/engine-atlas/inspect/:subRegionId"
                element={<EngineAtlasInspectRouter />}
              />
              <Route path="admin/engine-editor" element={<EngineEditor />} />
              <Route
                path="admin/engine-editor/weight-components"
                element={<EngineEditorWeightComponentsPage />}
              />
              <Route path="admin/users" element={<UserManagement />} />
              <Route path="admin/builds" element={<BuildTracker />} />
              <Route path="admin/errors" element={<ErrorLogViewer />} />
              <Route path="admin/algopix-jobs" element={<AlgopixJobMonitor />} />
              <Route path="admin/docs" element={<DocsCMS />} />
              <Route path="admin/releases" element={<ReleaseNotesManager />} />
              <Route path="admin/health" element={<SystemHealthDashboard />} />
              <Route path="admin/digest" element={<WeeklyDigest />} />
              <Route path="admin/runs" element={<JobRunsViewer />} />
              <Route path="*" element={<Navigate to="/admin/feedback" replace />} />
            </Route>
          </Routes>
        </AdminGate>
        <Analytics />
      </AppProvider>
    </BrowserRouter>
  );
}
